import { Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { DiagnosticAssessmentTableFilters } from '../AssessmentHistory/models';
import {
  getSkillsDiagnosticReport,
  getSkillReportSubstate,
  getStudentsCompletedDiagnosticInfo,
  getStudentsNotCompletedDiagnosticInfo,
} from '../../graphQLOperations/Diagnostics';
import type { DiagnosticsAllSkillsQuery } from '../../graphQLOperations/__generated__/DiagnosticsAllSkillsQuery.graphql';
import type { DiagnosticsSkillSubStateReportQuery } from '../../graphQLOperations/__generated__/DiagnosticsSkillSubStateReportQuery.graphql';
import type { DiagnosticsCompletedStudentsQuery } from '../../graphQLOperations/__generated__/DiagnosticsCompletedStudentsQuery.graphql';
import type { DiagnosticSkillPerformanceItem } from './SkillPerformanceByClassTable';
type AllSkills = NonNullable<DiagnosticsAllSkillsQuery['response']['allSkills']>;
type SkillNode = NonNullable<NonNullable<AllSkills['nodes']>[number]>;
type Student = { id: string; firstName: string; lastName: string; publicUserId: string };

export interface TemporarySkillInterface {
  id: string;
  code?: string | null;
  name?: string | null;
  types: Set<string>;
  unitsByType: Record<string, string[]>;
}

export interface QueryDiagnosticDataProp {
  filterState: DiagnosticAssessmentTableFilters;
  setSkillAssessments: Dispatch<SetStateAction<DiagnosticSkillPerformanceItem[]>>;
  setPlacedOutMap: Dispatch<SetStateAction<Record<string, Student[]>>>;
  setClassStudents: Dispatch<SetStateAction<Student[]>>;
  setLoading?: Dispatch<SetStateAction<boolean>>;
}
const normId = (id: string) => (id ? id.split(':').pop()! : id);

const normUnit = (t?: { name?: string | null; summary?: string | null } | null) => {
  if (!t?.name) return null;
  const label = t.summary ? `${t.name} - ${t.summary}` : t.name;
  return label.trim();
};
const canonicalizeType = (raw: string): string => {
  const CANON: Record<string, string> = { target: 'Target', support: 'Support', extension: 'Extension' };
  const v = raw.trim().toLowerCase();
  return CANON[v] ?? raw.trim();
};
const PRIORITY_MULTI = ['Target', 'Extension', 'Support'] as const;
const pickDisplayType = (types: Set<string>): string | null => {
  const arr = Array.from(types);
  if (arr.length === 0) return null;
  if (arr.length === 1) return arr[0];
  for (const p of PRIORITY_MULTI) if (types.has(p)) return p;
  return arr.sort((a, b) => a.localeCompare(b))[0];
};
const toTemp = (nodes: ReadonlyArray<SkillNode>): TemporarySkillInterface[] =>
  nodes.map(skill => {
    const types = new Set<string>();
    const unitsByType: Record<string, string[]> = {};
    (skill.targetSkills ?? []).forEach(ts => {
      const rawType = (ts?.targetSkillType?.name ?? '').trim();
      if (!rawType) return;
      const keyType = canonicalizeType(rawType);
      types.add(keyType);
      const unit = normUnit(ts?.target);
      if (!unit) return;
      if (!unitsByType[keyType]) unitsByType[keyType] = [];
      unitsByType[keyType].push(unit);
    });
    Object.keys(unitsByType).forEach(k => {
      const set = new Set(unitsByType[k]);
      unitsByType[k] = Array.from(set).sort((a, b) => a.localeCompare(b));
    });
    return { id: skill.id, code: skill.code ?? null, name: skill.name ?? null, types, unitsByType };
  });
export const DiagnosticReportByClass: FC<QueryDiagnosticDataProp> = ({
  filterState,
  setSkillAssessments,
  setPlacedOutMap,
  setClassStudents,
  setLoading,
}) => {
  const PAGE = 100;
  const serverKey = useMemo(
    () =>
      JSON.stringify({
        classes: filterState.filters.classes,
        roadmaps: filterState.filters.roadmaps,
        gradeLevels: filterState.filters.gradeLevels,
        skillGrades: filterState.filters.skillGrades,
      }),
    [
      filterState.filters.classes,
      filterState.filters.roadmaps,
      filterState.filters.gradeLevels,
      filterState.filters.skillGrades,
    ],
  );

  const [after, setAfter] = useState<string | null>(null);
  const [accSkills, setAccSkills] = useState<SkillNode[]>([]);
  const [chasing, setChasing] = useState(true);
  const seenCursors = useRef<Set<string>>(new Set());

  // This component is remounted (via `key={serverKey}` in the parent) whenever the
  // server-side filter set changes, so all the pagination state above starts fresh
  // and there is nothing to reset manually. Manual resets don't work here: the
  // queries below suspend on every filter change, and React discards the render
  // (throwing away any queued reset state) while refs stay mutated — which left the
  // accumulator and cursor stuck to the previous filter. We only flag the parent as
  // loading on mount; the commit effect clears it once the chase finishes.
  useEffect(() => {
    setLoading?.(true);
  }, []);
  const allSkillsPage = useLazyLoadQuery<DiagnosticsAllSkillsQuery>(
    getSkillsDiagnosticReport,
    {
      filter: {
        targetGroupIds: filterState.filters.roadmaps,
        standardGradeIds: filterState.filters.skillGrades,
      },
      targetGroupsIds: filterState.filters.roadmaps ?? [],
      first: PAGE,
      after,
    },
    {
      fetchPolicy: 'network-only',
      fetchKey: `SK:${serverKey}:${after ?? 'root'}`,
    },
  );

  useEffect(() => {
    const page = allSkillsPage?.allSkills;
    if (!page) return;

    const rawNodes = page.nodes ?? [];
    const nodes = rawNodes.filter(Boolean) as ReadonlyArray<SkillNode>;
    const { hasNextPage, endCursor } = page.pageInfo ?? { hasNextPage: false, endCursor: null };

    setAccSkills(prev => prev.concat(nodes));

    if (hasNextPage && endCursor && !seenCursors.current.has(endCursor)) {
      seenCursors.current.add(endCursor);
      setAfter(endCursor);
    } else {
      setChasing(false);
    }
  }, [allSkillsPage]);
  const [temporaryValues, setTemporaryValues] = useState<TemporarySkillInterface[]>([]);
  useEffect(() => {
    if (!chasing) setTemporaryValues(toTemp(accSkills));
  }, [chasing]);
  const skillIds = useMemo(
    () => (chasing ? [] : Array.from(new Set(temporaryValues.map(s => s.id)))),
    [chasing, temporaryValues],
  );
  const placedOutRes = useLazyLoadQuery<DiagnosticsSkillSubStateReportQuery>(
    getSkillReportSubstate,
    useMemo(
      () => ({
        filter: {
          skillIds,
          gradeLevelIds: filterState.filters.gradeLevels,
          classIds: filterState.filters.classes,
          skillResult: 'PLACED_OUT',
        },
        skip: skillIds.length === 0,
      }),
      [skillIds, filterState.filters.gradeLevels, filterState.filters.classes],
    ),
    { fetchPolicy: 'network-only', fetchKey: `PO:${serverKey}:${skillIds.join(',')}` },
  );
  const completed = useLazyLoadQuery<DiagnosticsCompletedStudentsQuery>(
    getStudentsCompletedDiagnosticInfo,
    useMemo(() => ({ classIds: filterState.filters.classes }), [filterState.filters.classes]),
    { fetchPolicy: 'network-only', fetchKey: `COMP:${serverKey}` },
  );
  const notCompleted = useLazyLoadQuery<DiagnosticsCompletedStudentsQuery>(
    getStudentsNotCompletedDiagnosticInfo,
    useMemo(() => ({ classIds: filterState.filters.classes }), [filterState.filters.classes]),
    { fetchPolicy: 'network-only', fetchKey: `NCOMP:${serverKey}` },
  );

  useEffect(() => {
    if (chasing) return;
    const rosterArr = [
      ...(completed?.allStudents?.nodes ?? []),
      ...(notCompleted?.allStudents?.nodes ?? []),
    ] as Student[];
    const rosterMap = new Map<string, Student>();
    rosterArr.forEach(s =>
      rosterMap.set(normId(s.id), {
        id: normId(s.id),
        firstName: s.firstName,
        lastName: s.lastName,
        publicUserId: s.publicUserId,
      }),
    );
    setClassStudents(Array.from(rosterMap.values()));
    const rows = placedOutRes?.studentsSkillSubStateReport ?? [];
    const placedMap = (rows ?? []).reduce<Record<string, Student[]>>((acc, r) => {
      const skillId = r.skill.id;
      const placedEntry = (r.usersBySkillResults ?? []).find(e => e?.key === 'PLACED_OUT');
      const currList = (placedEntry?.value ?? []).map(u => ({
        id: normId(u.id),
        firstName: u.firstName ?? '',
        lastName: u.lastName ?? '',
        publicUserId: u.publicUserId ?? '',
      })) as Student[];

      const dedup = new Map<string, Student>();
      (acc[skillId] ?? []).forEach(u => dedup.set(u.id, u));
      currList.forEach(u => dedup.set(u.id, u));

      acc[skillId] = Array.from(dedup.values()).sort(
        (a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName),
      );
      return acc;
    }, {});
    setPlacedOutMap(placedMap);
    const items: DiagnosticSkillPerformanceItem[] = temporaryValues.flatMap(s => {
      // A skill counts for this report unless it is *purely* Extension. Multi-type
      // skills (e.g. Support + Extension) stay because they contribute to the total.
      const hasNonExtension = Array.from(s.types).some(t => t !== 'Extension');
      if (s.types.size > 0 && !hasNonExtension) return [];

      // Extension is dropped from the type shown/filtered and from the units surfaced,
      // so it never leaks into the Type/Unit filters or the "+N more units" overlay.
      const nonExtensionTypes = new Set(Array.from(s.types).filter(t => t !== 'Extension'));
      const unitsByType = Object.fromEntries(Object.entries(s.unitsByType).filter(([type]) => type !== 'Extension'));
      const displayType = pickDisplayType(nonExtensionTypes);
      const visibleList = displayType ? unitsByType[displayType] ?? [] : [];
      const displayUnit = visibleList[0] ?? null;
      const totalUnits = Object.values(unitsByType).reduce((acc, arr) => acc + arr.length, 0);
      const moreCount = Math.max(0, totalUnits - (displayUnit ? 1 : 0));

      return [
        {
          skill: {
            id: s.id,
            code: s.code ?? null,
            name: s.name ?? null,
            displayType,
            displayUnit,
            moreCount,
            allUnitsByType: unitsByType,
          },
          placedOut: placedMap[s.id] ?? [],
        },
      ];
    });

    setSkillAssessments(items);
    setLoading?.(false);
  }, [
    chasing,
    temporaryValues,
    placedOutRes,
    completed,
    notCompleted,
    setPlacedOutMap,
    setClassStudents,
    setSkillAssessments,
    setLoading,
  ]);
  return <div style={{ display: 'none' }} />;
};
